// ════════════════════════════════════════════════════════════════════════════
//  ManDana — pursuits / مساعي
//  ---------------------------------------------------------------------------
//  A pursuit is enduring striving inside one domain. Career holds ManDana;
//  ManDana holds finite projects such as Launch Manifestations 1.2. A pursuit
//  can be rested, but it has no target date and is never "completed".
//
//  Projects point upward with `pursuitId`, making the pursuit and everything
//  below it one containment tree. Deleting a pursuit moves that whole tree to
//  Trash: the work goes with the pursuit and comes back with it on restore.
//
//  BUILT-IN PURSUITS ARE NEVER STORED. IQAMA ships with the app, so it is
//  materialised on read rather than written into the index at first launch —
//  the same pattern md-project.ts uses for Prayers. Nothing is seeded, which
//  means an account that has never opened the app already has it, an account
//  syncing from an older device cannot lose it, and its name and domain travel
//  with the code rather than with the data. `isRow` refuses any stored row
//  wearing a built-in id so a stray write or a bad merge can never become it.
//
//  LOCKED IS ABOUT STRUCTURE, NOT ABOUT WRITING. A built-in cannot be renamed,
//  moved, rested or deleted — those are the app's to say. What the user writes
//  in it is theirs, and it is stored and synced like anyone else's.
// ════════════════════════════════════════════════════════════════════════════

/* The same vocabulary md-project.ts speaks, so a screen holding both does not
   need two ways of reading a refusal. */
type MdPursuitRefusal = 'admin' | 'builtin' | 'missing' | 'invalid' | 'storage_full';
type MdPursuitResult = { ok: true } | { ok: false; reason: MdPursuitRefusal };

interface MdPursuit {
  id: string;
  name: string;
  domainId: string;
  at: number;
  closed?: boolean;
  builtin?: boolean;
}

(function (g: any) {
  'use strict';

  const K_INDEX = 'md:pursuits';
  const K_PURSUIT = 'md:pursuit:';
  const NAME_MAX = 60;

  /* Ships with the app; see the header. Spirit / e6. */
  const IQAMA: MdPursuit = { id: 'iqama', name: 'IQAMA', domainId: 'e6', at: 0, builtin: true };
  const BUILTINS: MdPursuit[] = [IQAMA];
  const BUILTIN_IDS = new Set(BUILTINS.map((row) => row.id));

  /** The same question md-project.ts asks, for the same reason and in the same
      word. See md-admin-door.ts. */
  const adminHeld = (id: unknown): boolean =>
    typeof id === 'string' && BUILTIN_IDS.has(id)
    && !(typeof g.mdAdminIs === 'function' && g.mdAdminIs());

  /* ── a built-in's identity, on its own document ───────────────────────────
     IQAMA is defined in code, which is what makes it dependable — and for a
     long time also meant it was not a document, and so could not be found by a
     search built over documents. Its NAME now lives on `md:pursuit:iqama`,
     which already existed for its writing and already syncs.

     One list, so the three places below never name the field by hand. An
     explicit null is "nothing set here" and the shipped name comes back, the
     same rule md-project.ts keeps for a cleared status. */
  const IDENTITY_FIELDS = ['name'];

  /* Guarded for the same reason md-domain.ts is: a lookup must not require the
     chokepoint to have loaded. No storage means no overlay, and the shipped
     name is the answer. */
  const readDoc = (key: string): any =>
    (typeof g.mdRead === 'function' ? g.mdRead(key, null) : null);

  function identityOf(raw: any): Record<string, any> | null {
    if (!raw || typeof raw !== 'object') return null;
    const out: Record<string, any> = {};
    let any = false;
    for (const field of IDENTITY_FIELDS) {
      if (!(field in raw)) continue;
      out[field] = raw[field];
      any = true;
    }
    return any ? out : null;
  }

  function materialiseBuiltin(row: MdPursuit): MdPursuit {
    const said = identityOf(readDoc(K_PURSUIT + row.id));
    if (!said) return row;
    const out: Record<string, any> = { ...row };
    for (const field of IDENTITY_FIELDS) {
      const v = said[field];
      if (typeof v === 'string' && v.trim()) out[field] = v;
    }
    return out as MdPursuit;
  }

  /** Give IQAMA a document to be found by. See md-builtins.ts. The clock starts
      at 0 on a new document so the app's own statement about what it ships can
      never outrank a real edit arriving from another device. */
  function mdPursuitMaterialiseBuiltins(): number {
    if (typeof g.mdWrite !== 'function') return 0;
    let wrote = 0;
    for (const row of BUILTINS) {
      const home = readDoc(K_PURSUIT + row.id);
      const held = home && typeof home === 'object' ? home : null;
      if (held && 'name' in held) continue;
      const at = held && Number.isFinite(held.at) ? held.at : 0;
      if (g.mdWrite(K_PURSUIT + row.id, { ...(held || {}), name: row.name, at })) wrote += 1;
    }
    return wrote;
  }

  /* Ids shorter than the six characters newId() produces, which the sync walk
     would otherwise skip — the writing inside a built-in must travel too. */
  function syncId(id: string): boolean {
    return /^[a-z0-9]{6,40}$/.test(id) || BUILTIN_IDS.has(id);
  }

  function newId(): string {
    let s = '';
    while (s.length < 10) s += Math.random().toString(36).slice(2).replace(/[^a-z0-9]/g, '');
    return s.slice(0, 10);
  }

  function isRow(row: any): row is MdPursuit {
    return !!row && typeof row === 'object'
      && typeof row.id === 'string' && /^[a-z0-9]{6,40}$/.test(row.id)
      && !BUILTIN_IDS.has(row.id)              // never let a stored row shadow a built-in
      && typeof row.name === 'string' && !!row.name.trim()
      && typeof row.domainId === 'string' && !!g.mdDomainById(row.domainId);
  }

  function readIndex(): MdPursuit[] {
    const raw = g.mdRead(K_INDEX, []);
    return Array.isArray(raw) ? raw.filter(isRow) : [];
  }

  function writeIndex(rows: MdPursuit[]): boolean {
    return g.mdWrite(K_INDEX, rows);
  }

  function mdPursuitIndex(opts?: { open?: boolean; domainId?: string }): MdPursuit[] {
    let rows = [...BUILTINS.map(materialiseBuiltin), ...readIndex()];
    if (opts && opts.open) rows = rows.filter((row) => !row.closed);
    if (opts && opts.domainId) rows = rows.filter((row) => row.domainId === opts.domainId);
    return rows;
  }

  /* Keyed for the same reason md-project.ts's lookup is, and by the same rule:
     this is the second rung of the walk every row of the Main DB makes to name
     its Parent, so it is asked once per record and the index behind it has not
     moved between the first and the last. Retired by mdStorageRevision(). The
     write path below still reads its own copy, because its callers mutate a row
     in place and write the array back, and a write that fails must not survive
     in memory as if it had succeeded. */
  let byId: { revision: number; map: Map<string, MdPursuit> } | null = null;
  function indexById(): Map<string, MdPursuit> {
    const revision = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision() : null;
    if (byId && revision !== null && byId.revision === revision) return byId.map;
    const map = new Map<string, MdPursuit>();
    for (const row of readIndex()) if (!map.has(row.id)) map.set(row.id, row);
    if (revision !== null) byId = { revision, map };
    return map;
  }

  function mdPursuitById(id: unknown): MdPursuit | null {
    if (typeof id !== 'string') return null;
    if (BUILTIN_IDS.has(id)) {
      const row = BUILTINS.find((r) => r.id === id);
      return row ? materialiseBuiltin(row) : null;
    }
    const row = indexById().get(id);
    return row ? { ...row } : null;      // the map's own row never leaves the map
  }

  function mdPursuitCreate(name: unknown, domainId: unknown): MdPursuit | null {
    const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
    if (!clean || !g.mdDomainById(domainId)) return null;
    const row: MdPursuit = { id: newId(), name: clean, domainId: domainId as string, at: Date.now() };
    return writeIndex([...readIndex(), row]) ? row : null;
  }

  function mdPursuitRename(id: unknown, name: unknown): boolean {
    if (adminHeld(id)) return false;
    if (typeof id === 'string' && BUILTIN_IDS.has(id)) {
      /* No index row to rename — the name is an overlay on the home document,
         and an empty one is a rename back to what the app ships. NEVER a null:
         a document with no name is a document search cannot find, which is the
         bug this change exists to fix. See md-project.ts, same reason. */
      const shipped = BUILTINS.find((row) => row.id === id);
      const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
      const home = readDoc(K_PURSUIT + id);
      const held = home && typeof home === 'object' ? home : {};
      return g.mdWrite(K_PURSUIT + id,
        { ...held, name: clean || (shipped ? shipped.name : null), at: Date.now() });
    }
    const rows = readIndex();
    const row = rows.find((item) => item.id === id);
    const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
    if (!row || !clean) return false;
    row.name = clean;
    return writeIndex(rows);
  }

  /* ── the refusal, said out loud ───────────────────────────────────────────
     Every mutator here answers `false` and leaves the caller to guess between
     "that is a built-in", "that is not a name" and "this device is full". A
     record screen whose title field has to put a refused draft back needs to
     know which — so this module gets the same door shape md-project.ts already
     uses. A DOOR ONTO THE SETTER, not a second copy of it. */
  function writeRefusal(): MdPursuitResult {
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'invalid' };
  }

  function mdPursuitTryRename(id: unknown, name: unknown): MdPursuitResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdPursuitById(id)) return { ok: false, reason: 'missing' };
    if (!(typeof name === 'string' ? name : '').trim()
      && !(typeof id === 'string' && BUILTIN_IDS.has(id))) return { ok: false, reason: 'invalid' };
    return mdPursuitRename(id, name) ? { ok: true } : writeRefusal();
  }

  /** Set down for a season, or taken up again. A pursuit is never "completed"
      (md-pursuit.ts's first paragraph), so this is rest, not a finish line. */
  function mdPursuitTrySetClosed(id: unknown, closed: unknown): MdPursuitResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdPursuitById(id)) return { ok: false, reason: 'missing' };
    return mdPursuitSetClosed(id, !!closed) ? { ok: true } : writeRefusal();
  }

  /** The low-level row removal used by the administrative refusal door. A
      person's pursuit leaves through mdTrashDelete instead, so its work goes
      to Trash with it and comes back with it on restore. */
  function mdPursuitTryDelete(id: unknown): MdPursuitResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdPursuitById(id)) return { ok: false, reason: 'missing' };
    return mdPursuitDelete(id) ? { ok: true } : writeRefusal();
  }

  /** Move a pursuit to another part of life — and with it, by mdProjectDomainFor,
      every project it holds. */
  function mdPursuitTrySetDomain(id: unknown, domainId: unknown): MdPursuitResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!g.mdDomainById(domainId)) return { ok: false, reason: 'invalid' };
    if (!mdPursuitById(id)) return { ok: false, reason: 'missing' };
    return mdPursuitSetDomain(id, domainId) ? { ok: true } : writeRefusal();
  }

  function mdPursuitSetDomain(id: unknown, domainId: unknown): boolean {
    if (adminHeld(id)) return false;
    if (!g.mdDomainById(domainId)) return false;
    const rows = readIndex();
    const row = rows.find((item) => item.id === id);
    if (!row) return false;
    row.domainId = domainId as string;
    return writeIndex(rows);
  }

  function mdPursuitSetClosed(id: unknown, closed: boolean): boolean {
    if (adminHeld(id)) return false;
    const rows = readIndex();
    const row = rows.find((item) => item.id === id);
    if (!row) return false;
    row.closed = !!closed;
    return writeIndex(rows);
  }

  function mdPursuitDelete(id: unknown): boolean {
    if (adminHeld(id)) return false;
    const rows = readIndex();
    if (!rows.some((row) => row.id === id)) return false;
    g.mdRemove(K_PURSUIT + id);
    return writeIndex(rows.filter((row) => row.id !== id));
  }

  function mdPursuitTextRead(id: unknown): string {
    if (!mdPursuitById(id)) return '';
    const raw = g.mdRead(K_PURSUIT + id, null);
    return raw && typeof raw === 'object' && typeof raw.text === 'string' ? raw.text : '';
  }

  /** Where this pursuit's writing is kept, and when it was last written — `at`
      is 0 when nothing is written there. The IQAMA pursuit's identity is code
      and its writing is an ordinary document, so the Librarian's X-ray has to
      ask about both; see mdDomainWriting for the whole argument. */
  function mdPursuitWriting(id: unknown): { key: string; at: number } {
    if (!mdPursuitById(id)) return { key: '', at: 0 };
    const raw = g.mdRead(K_PURSUIT + id, null);
    const at = raw && typeof raw === 'object' && typeof raw.at === 'number' ? raw.at : 0;
    return { key: K_PURSUIT + id, at: Number.isFinite(at) ? at : 0 };
  }

  function mdPursuitLinksRead(id: unknown): unknown[] {
    const raw = typeof id === 'string' ? g.mdRead(K_PURSUIT + id, null) : null;
    return raw && typeof raw === 'object' && g.mdWikiBindings
      ? g.mdWikiBindings(raw.links, raw.text) : [];
  }

  function mdPursuitTextWrite(id: unknown, text: unknown, links?: unknown): boolean {
    if (!mdPursuitById(id)) return false;
    const body = typeof text === 'string' ? text : '';
    const previous = g.mdRead(K_PURSUIT + id, null);
    /* A cleared paragraph is not a rename. Before identity lived here the whole
       document could go; now it may only go if there is nothing else on it. */
    if (!body.trim()) {
      const held = identityOf(previous);
      if (!held) return g.mdRemove(K_PURSUIT + id);
      return g.mdWrite(K_PURSUIT + id, { ...held, at: Date.now() });
    }
    const carried = links === undefined && g.mdWikiReconcile && previous
      ? g.mdWikiReconcile(previous.text, body, previous.links) : links;
    const clean = g.mdWikiBindings ? g.mdWikiBindings(carried, body) : [];
    return g.mdWrite(K_PURSUIT + id, { ...(identityOf(previous) || {}), text: body,
      ...(clean.length ? { links: clean } : {}), at: Date.now() });
  }

  function mdPursuitTemplate(): string {
    return '## The striving\n\n\n## The principles\n\n\n## Horizons\n\n';
  }

  function mdPursuitSeedText(name: unknown): string {
    const clean = (typeof name === 'string' ? name : '').trim();
    return (clean ? clean + '\n\n' : '') + mdPursuitTemplate();
  }

  function mdPursuitSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const rows = readIndex();
    if (rows.length) out.pursuits = rows;
    for (const key of g.mdStorageKeys(K_PURSUIT)) {
      const id = key.slice(K_PURSUIT.length);
      if (!syncId(id)) continue;
      const raw = g.mdRead(key, null);
      // Travels when it holds writing OR an identity somebody set — the second
      // half is what carries IQAMA's name to a second device, since a built-in
      // has no index row to carry it.
      if (raw && typeof raw === 'object'
        && ((typeof raw.text === 'string' && raw.text.trim()) || identityOf(raw))) {
        out[`pursuit:${id}`] = raw;
      }
    }
    return out;
  }

  function mdPursuitApplyDoc(key: unknown, value: unknown): boolean {
    if (key === 'pursuits') {
      if (value == null) return g.mdRemove(K_INDEX);
      return Array.isArray(value) ? writeIndex(value.filter(isRow)) : false;
    }
    if (typeof key !== 'string' || !key.startsWith('pursuit:')) return false;
    const id = key.slice('pursuit:'.length);
    if (!syncId(id)) return false;
    const stored = K_PURSUIT + id;
    if (value == null) return g.mdRemove(stored);
    if (!value || typeof value !== 'object') return false;
    const incoming = value as { text?: unknown; links?: unknown; at?: unknown };
    const said = identityOf(incoming);
    const wrote = typeof incoming.text === 'string';
    // A document carrying only a name is a whole document now — that is how a
    // built-in's name arrives from a device where nothing was written beside it.
    if ((!wrote && !said) || !Number.isFinite(incoming.at)) return false;
    const mine = g.mdRead(stored, null);
    if (mine && Number.isFinite(mine.at) && mine.at > (incoming.at as number)) return true;
    /* IDENTITY MERGES ONE FIELD AT A TIME, the way md-project.ts has always
       merged a home document: the sender speaks for a field only if it mentions
       it, so a device with only a paragraph to send cannot erase a name. This
       path used to replace the document wholesale, which would have done
       exactly that. */
    const identity = { ...(identityOf(mine) || {}), ...(said || {}) };
    const body = wrote ? (incoming.text as string) : '';
    if (!body.trim()) {
      if (!Object.keys(identity).length) return g.mdRemove(stored);
      return g.mdWrite(stored, { ...identity, at: incoming.at });
    }
    const clean = g.mdWikiBindings ? g.mdWikiBindings(incoming.links, body) : [];
    return g.mdWrite(stored, { ...identity, text: body,
      ...(clean.length ? { links: clean } : {}), at: incoming.at });
  }

  g.mdPursuitMaterialiseBuiltins = mdPursuitMaterialiseBuiltins;
  g.MD_PURSUIT_BUILTIN_IDS = [...BUILTIN_IDS];
  Object.assign(g, { mdPursuitIndex, mdPursuitById, mdPursuitCreate, mdPursuitRename, mdPursuitTryRename,
    mdPursuitTrySetClosed, mdPursuitTryDelete, mdPursuitTrySetDomain,
    mdPursuitSetDomain, mdPursuitSetClosed, mdPursuitDelete,
    mdPursuitTextRead, mdPursuitWriting, mdPursuitLinksRead, mdPursuitTextWrite,
    mdPursuitTemplate, mdPursuitSeedText, mdPursuitSyncDocs, mdPursuitApplyDoc });
})(typeof window !== 'undefined' ? window : globalThis);
